import { localizeWithPath } from '../../../../nls.js';
import { onUnexpectedError } from '../../../../base/common/errors.js';
import Severity$1 from '../../../../base/common/severity.js';
import { EXTENSION_IDENTIFIER_PATTERN } from '../../../../platform/extensionManagement/common/extensionManagement.js';
import { Extensions } from '../../../../platform/jsonschemas/common/jsonContributionRegistry.js';
import { Registry } from '../../../../platform/registry/common/platform.js';
import { EXTENSION_CATEGORIES, ExtensionIdentifierSet } from '../../../../platform/extensions/common/extensions.js';
import { allApiProposals } from './extensionsApiProposals.js';
import { productSchemaId } from '../../../../platform/product/common/productService.js';
import { ImplicitActivationEvents } from '../../../../platform/extensionManagement/common/implicitActivationEvents.js';
const _moduleId = "vs/workbench/services/extensions/common/extensionsRegistry";
const schemaRegistry = ( (Registry.as(Extensions.JSONContribution)));
class ExtensionMessageCollector {
    constructor(messageHandler, extension, extensionPointId) {
        this._messageHandler = messageHandler;
        this._extension = extension;
        this._extensionPointId = extensionPointId;
    }
    _msg(type, message) {
        this._messageHandler({
            type: type,
            message: message,
            extensionId: this._extension.identifier,
            extensionPointId: this._extensionPointId
        });
    }
    error(message) {
        this._msg(Severity$1.Error, message);
    }
    warn(message) {
        this._msg(Severity$1.Warning, message);
    }
    info(message) {
        this._msg(Severity$1.Info, message);
    }
}
class ExtensionPointUserDelta {
    static _toSet(arr) {
        const result = ( (new ExtensionIdentifierSet()));
        for (let i = 0, len = arr.length; i < len; i++) {
            result.add(arr[i].description.identifier);
        }
        return result;
    }
    static compute(previous, current) {
        if (!previous || !previous.length) {
            return (
                 (new ExtensionPointUserDelta(current, []))
            );
        }
        if (!current || !current.length) {
            return (
                 (new ExtensionPointUserDelta([], previous))
            );
        }
        const previousSet = this._toSet(previous);
        const currentSet = this._toSet(current);
        const added = current.filter(user => !( (previousSet.has(user.description.identifier))));
        const removed = previous.filter(user => !( (currentSet.has(user.description.identifier))));
        return (
             (new ExtensionPointUserDelta(added, removed))
        );
    }
    constructor(added, removed) {
        this.added = added;
        this.removed = removed;
    }
}
class ExtensionPoint {
    constructor(name, defaultExtensionKind) {
        this.name = name;
        this.defaultExtensionKind = defaultExtensionKind;
        this._handler = null;
        this._users = null;
        this._delta = null;
    }
    setHandler(handler) {
        if (this._handler !== null) {
            throw ( (new Error('Handler already set!')));
        }
        this._handler = handler;
        this._handle();
    }
    acceptUsers(users) {
        this._delta = ExtensionPointUserDelta.compute(this._users, users);
        this._users = users;
        this._handle();
    }
    _handle() {
        if (this._handler === null || this._users === null || this._delta === null) {
            return;
        }
        try {
            this._handler(this._users, this._delta);
        }
        catch (err) {
            onUnexpectedError(err);
        }
    }
}
const extensionKindSchema = {
    type: 'string',
    enum: [
        'ui',
        'workspace'
    ],
    enumDescriptions: [
        ( localizeWithPath(
            _moduleId,
            0,
            "UI extension kind. In a remote window, such extensions are enabled only when available on the local machine."
        )),
        ( localizeWithPath(
            _moduleId,
            1,
            "Workspace extension kind. In a remote window, such extensions are enabled only when available on the remote."
        )),
    ],
};
const schemaId = 'vscode://schemas/vscode-extensions';
const schema = {
    properties: {
        engines: {
            type: 'object',
            description: ( localizeWithPath(_moduleId, 2, "Engine compatibility.")),
            properties: {
                'vscode': {
                    type: 'string',
                    description: ( localizeWithPath(
                        _moduleId,
                        3,
                        'For VS Code extensions, specifies the VS Code version that the extension is compatible with. Cannot be *. For example: ^0.10.5 indicates compatibility with a minimum VS Code version of 0.10.5.'
                    )),
                    default: '^1.22.0',
                }
            }
        },
        publisher: {
            description: ( localizeWithPath(_moduleId, 4, 'The publisher of the VS Code extension.')),
            type: 'string'
        },
        displayName: {
            description: ( localizeWithPath(
                _moduleId,
                5,
                'The display name for the extension used in the VS Code gallery.'
            )),
            type: 'string'
        },
        categories: {
            description: ( localizeWithPath(
                _moduleId,
                6,
                'The categories used by the VS Code gallery to categorize the extension.'
            )),
            type: 'array',
            uniqueItems: true,
            items: {
                oneOf: [{
                        type: 'string',
                        enum: EXTENSION_CATEGORIES,
                    },
                    {
                        type: 'string',
                        const: 'Languages',
                        deprecationMessage: ( localizeWithPath(_moduleId, 7, 'Use \'Programming  Languages\' instead')),
                    }]
            }
        },
        galleryBanner: {
            type: 'object',
            description: ( localizeWithPath(_moduleId, 8, 'Banner used in the VS Code marketplace.')),
            properties: {
                color: {
                    description: ( localizeWithPath(_moduleId, 9, 'The banner color on the VS Code marketplace page header.')),
                    type: 'string'
                },
                theme: {
                    description: ( localizeWithPath(_moduleId, 10, 'The color theme for the font used in the banner.')),
                    type: 'string',
                    enum: ['dark', 'light']
                }
            }
        },
        contributes: {
            description: ( localizeWithPath(
                _moduleId,
                11,
                'All contributions of the VS Code extension represented by this package.'
            )),
            type: 'object',
            properties: {
            },
            default: {}
        },
        preview: {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                12,
                'Sets the extension to be flagged as a Preview in the Marketplace.'
            )),
        },
        enableProposedApi: {
            type: 'boolean',
            deprecationMessage: ( localizeWithPath(_moduleId, 13, 'Use `enabledApiProposals` instead.')),
        },
        enabledApiProposals: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                14,
                'Enable API proposals to try them out. Only valid **during development**. Extensions **cannot be published** with this property. For more details visit: https://code.visualstudio.com/api/advanced-topics/using-proposed-api'
            )),
            type: 'array',
            uniqueItems: true,
            items: {
                type: 'string',
                enum: ( (Object.keys(allApiProposals))),
                markdownEnumDescriptions: ( (Object.values(allApiProposals)))
            }
        },
        api: {
            markdownDescription: ( localizeWithPath(
                _moduleId,
                15,
                'Describe the API provided by this extension. For more details visit: https://code.visualstudio.com/api/advanced-topics/remote-extensions#handling-dependencies-with-remote-extensions'
            )),
            type: 'string',
            enum: ['none'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    16,
                    "Give up entirely the ability to export any APIs. This allows other extensions that depend on this extension to run in a separate extension host process or in a remote machine."
                ))
            ]
        },
        activationEvents: {
            description: ( localizeWithPath(_moduleId, 17, 'Activation events for the VS Code extension.')),
            type: 'array',
            items: {
                type: 'string',
                defaultSnippets: [
                    {
                        label: 'onWebviewPanel',
                        description: ( localizeWithPath(
                            _moduleId,
                            18,
                            'An activation event emmited when a webview is loaded of a certain viewType'
                        )),
                        body: 'onWebviewPanel:viewType'
                    },
                    {
                        label: 'onLanguage',
                        description: ( localizeWithPath(
                            _moduleId,
                            19,
                            'An activation event emitted whenever a file that resolves to the specified language gets opened.'
                        )),
                        body: 'onLanguage:${1:languageId}'
                    },
                    {
                        label: 'onCommand',
                        description: ( localizeWithPath(
                            _moduleId,
                            20,
                            'An activation event emitted whenever the specified command gets invoked.'
                        )),
                        body: 'onCommand:${2:commandId}'
                    },
                    {
                        label: 'onDebug',
                        description: ( localizeWithPath(
                            _moduleId,
                            21,
                            'An activation event emitted whenever a user is about to start debugging or about to setup debug configurations.'
                        )),
                        body: 'onDebug'
                    },
                    {
                        label: 'onDebugInitialConfigurations',
                        description: ( localizeWithPath(
                            _moduleId,
                            22,
                            'An activation event emitted whenever a "launch.json" needs to be created (and all provideDebugConfigurations methods need to be called).'
                        )),
                        body: 'onDebugInitialConfigurations'
                    },
                    {
                        label: 'onDebugDynamicConfigurations',
                        description: ( localizeWithPath(
                            _moduleId,
                            23,
                            'An activation event emitted whenever a list of all debug configurations needs to be created (and all provideDebugConfigurations methods for the "dynamic" scope need to be called).'
                        )),
                        body: 'onDebugDynamicConfigurations'
                    },
                    {
                        label: 'onDebugResolve',
                        description: ( localizeWithPath(
                            _moduleId,
                            24,
                            'An activation event emitted whenever a debug session with the specific type is about to be launched (and a corresponding resolveDebugConfiguration method needs to be called).'
                        )),
                        body: 'onDebugResolve:${6:type}'
                    },
                    {
                        label: 'onDebugAdapterProtocolTracker',
                        description: ( localizeWithPath(
                            _moduleId,
                            25,
                            'An activation event emitted whenever a debug session with the specific type is about to be launched and a debug protocol tracker might be needed.'
                        )),
                        body: 'onDebugAdapterProtocolTracker:${6:type}'
                    },
                    {
                        label: 'workspaceContains',
                        description: ( localizeWithPath(
                            _moduleId,
                            26,
                            'An activation event emitted whenever a folder is opened that contains at least a file matching the specified glob pattern.'
                        )),
                        body: 'workspaceContains:${4:filePattern}'
                    },
                    {
                        label: 'onStartupFinished',
                        description: ( localizeWithPath(
                            _moduleId,
                            27,
                            'An activation event emitted after the start-up finished (after all `*` activated extensions have finished activating).'
                        )),
                        body: 'onStartupFinished'
                    },
                    {
                        label: 'onTaskType',
                        description: ( localizeWithPath(
                            _moduleId,
                            28,
                            'An activation event emitted whenever tasks of a certain type need to be listed or resolved.'
                        )),
                        body: 'onTaskType:${1:taskType}'
                    },
                    {
                        label: 'onFileSystem',
                        description: ( localizeWithPath(
                            _moduleId,
                            29,
                            'An activation event emitted whenever a file or folder is accessed with the given scheme.'
                        )),
                        body: 'onFileSystem:${1:scheme}'
                    },
                    {
                        label: 'onEditSession',
                        description: ( localizeWithPath(
                            _moduleId,
                            30,
                            'An activation event emitted whenever an edit session is accessed with the given scheme.'
                        )),
                        body: 'onEditSession:${1:scheme}'
                    },
                    {
                        label: 'onSearch',
                        description: ( localizeWithPath(
                            _moduleId,
                            31,
                            'An activation event emitted whenever a search is started in the folder with the given scheme.'
                        )),
                        body: 'onSearch:${7:scheme}'
                    },
                    {
                        label: 'onView',
                        body: 'onView:${5:viewId}',
                        description: ( localizeWithPath(
                            _moduleId,
                            32,
                            'An activation event emitted whenever the specified view is expanded.'
                        )),
                    },
                    {
                        label: 'onUri',
                        body: 'onUri',
                        description: ( localizeWithPath(
                            _moduleId,
                            33,
                            'An activation event emitted whenever a system-wide Uri directed towards this extension is open.'
                        )),
                    },
                    {
                        label: 'onOpenExternalUri',
                        body: 'onOpenExternalUri',
                        description: ( localizeWithPath(
                            _moduleId,
                            34,
                            'An activation event emitted whenever a external uri (such as an http or https link) is being opened.'
                        )),
                    },
                    {
                        label: 'onCustomEditor',
                        body: 'onCustomEditor:${9:viewType}',
                        description: ( localizeWithPath(
                            _moduleId,
                            35,
                            'An activation event emitted whenever the specified custom editor becomes visible.'
                        )),
                    },
                    {
                        label: 'onNotebook',
                        body: 'onNotebook:${1:type}',
                        description: ( localizeWithPath(
                            _moduleId,
                            36,
                            'An activation event emitted whenever the specified notebook document is opened.'
                        )),
                    },
                    {
                        label: 'onAuthenticationRequest',
                        body: 'onAuthenticationRequest:${11:authenticationProviderId}',
                        description: ( localizeWithPath(
                            _moduleId,
                            37,
                            'An activation event emitted whenever sessions are requested from the specified authentication provider.'
                        ))
                    },
                    {
                        label: 'onRenderer',
                        description: ( localizeWithPath(
                            _moduleId,
                            38,
                            'An activation event emitted whenever a notebook output renderer is used.'
                        )),
                        body: 'onRenderer:${11:rendererId}'
                    },
                    {
                        label: 'onTerminalProfile',
                        body: 'onTerminalProfile:${1:terminalId}',
                        description: ( localizeWithPath(
                            _moduleId,
                            39,
                            'An activation event emitted when a specific terminal profile is launched.'
                        )),
                    },
                    {
                        label: 'onTerminalQuickFixRequest',
                        body: 'onTerminalQuickFixRequest:${1:quickFixId}',
                        description: ( localizeWithPath(
                            _moduleId,
                            40,
                            'An activation event emitted when a command matches the selector associated with this ID'
                        )),
                    },
                    {
                        label: 'onWalkthrough',
                        body: 'onWalkthrough:${1:walkthroughID}',
                        description: ( localizeWithPath(
                            _moduleId,
                            41,
                            'An activation event emitted when a specified walkthrough is opened.'
                        )),
                    },
                    {
                        label: 'onIssueReporterOpened',
                        body: 'onIssueReporterOpened',
                        description: ( localizeWithPath(
                            _moduleId,
                            42,
                            'An activation event emitted when the issue reporter is opened.'
                        )),
                    },
                    {
                        label: '*',
                        description: ( localizeWithPath(
                            _moduleId,
                            43,
                            'An activation event emitted on VS Code startup. To ensure a great end user experience, please use this activation event in your extension only when no other activation events combination works in your use-case.'
                        )),
                        body: '*'
                    }
                ],
            }
        },
        badges: {
            type: 'array',
            description: ( localizeWithPath(
                _moduleId,
                44,
                'Array of badges to display in the sidebar of the Marketplace\'s extension page.'
            )),
            items: {
                type: 'object',
                required: ['url', 'href', 'description'],
                properties: {
                    url: {
                        type: 'string',
                        description: ( localizeWithPath(_moduleId, 45, 'Badge image URL.'))
                    },
                    href: {
                        type: 'string',
                        description: ( localizeWithPath(_moduleId, 46, 'Badge link.'))
                    },
                    description: {
                        type: 'string',
                        description: ( localizeWithPath(_moduleId, 47, 'Badge description.'))
                    }
                }
            }
        },
        markdown: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                48,
                "Controls the Markdown rendering engine used in the Marketplace. Either github (default) or standard."
            )),
            enum: ['github', 'standard'],
            default: 'github'
        },
        qna: {
            default: 'marketplace',
            description: ( localizeWithPath(
                _moduleId,
                49,
                "Controls the Q&A link in the Marketplace. Set to marketplace to enable the default Marketplace Q & A site. Set to a string to provide the URL of a custom Q & A site. Set to false to disable Q & A altogether."
            )),
            anyOf: [
                {
                    type: ['string', 'boolean'],
                    enum: ['marketplace', false]
                },
                {
                    type: 'string'
                }
            ]
        },
        extensionDependencies: {
            description: ( localizeWithPath(
                _moduleId,
                50,
                'Dependencies to other extensions. The identifier of an extension is always ${publisher}.${name}. For example: vscode.csharp.'
            )),
            type: 'array',
            uniqueItems: true,
            items: {
                type: 'string',
                pattern: EXTENSION_IDENTIFIER_PATTERN
            }
        },
        extensionPack: {
            description: ( localizeWithPath(
                _moduleId,
                51,
                "A set of extensions that can be installed together. The identifier of an extension is always ${publisher}.${name}. For example: vscode.csharp."
            )),
            type: 'array',
            uniqueItems: true,
            items: {
                type: 'string',
                pattern: EXTENSION_IDENTIFIER_PATTERN
            }
        },
        extensionKind: {
            description: ( localizeWithPath(
                _moduleId,
                52,
                "Define the kind of an extension. `ui` extensions are installed and run on the local machine while `workspace` extensions run on the remote."
            )),
            type: 'array',
            items: extensionKindSchema,
            default: ['workspace'],
            defaultSnippets: [
                {
                    body: ['ui'],
                    description: ( localizeWithPath(
                        _moduleId,
                        53,
                        "Define an extension which can run only on the local machine when connected to remote window."
                    ))
                },
                {
                    body: ['workspace'],
                    description: ( localizeWithPath(
                        _moduleId,
                        54,
                        "Define an extension which can run only on the remote machine when connected remote window."
                    ))
                },
                {
                    body: ['ui', 'workspace'],
                    description: ( localizeWithPath(
                        _moduleId,
                        55,
                        "Define an extension which can run on either side, with a preference towards running on the local machine."
                    ))
                },
                {
                    body: ['workspace', 'ui'],
                    description: ( localizeWithPath(
                        _moduleId,
                        56,
                        "Define an extension which can run on either side, with a preference towards running on the remote machine."
                    ))
                },
                {
                    body: [],
                    description: ( localizeWithPath(
                        _moduleId,
                        57,
                        "Define an extension which cannot run in a remote context, neither on the local, nor on the remote machine."
                    ))
                }
            ]
        },
        capabilities: {
            description: ( localizeWithPath(
                _moduleId,
                58,
                "Declare the set of supported capabilities by the extension."
            )),
            type: 'object',
            properties: {
                virtualWorkspaces: {
                    description: ( localizeWithPath(
                        _moduleId,
                        59,
                        "Declares whether the extension should be enabled in virtual workspaces. A virtual workspace is a workspace which is not backed by any on-disk resources. When false, this extension will be automatically disabled in virtual workspaces. Default is true."
                    )),
                    type: ['boolean', 'object'],
                    defaultSnippets: [
                        { label: 'limited', body: { supported: '${1:limited}', description: '${2}' } },
                        { label: 'false', body: { supported: false, description: '${2}' } },
                    ],
                    default: true.valueOf,
                    properties: {
                        supported: {
                            markdownDescription: ( localizeWithPath(
                                _moduleId,
                                60,
                                "Declares the level of support for virtual workspaces by the extension."
                            )),
                            type: ['string', 'boolean'],
                            enum: ['limited', true, false],
                            enumDescriptions: [
                                ( localizeWithPath(
                                    _moduleId,
                                    61,
                                    "The extension will be enabled in virtual workspaces with some functionality disabled."
                                )),
                                ( localizeWithPath(
                                    _moduleId,
                                    62,
                                    "The extension will be enabled in virtual workspaces with all functionality enabled."
                                )),
                                ( localizeWithPath(_moduleId, 63, "The extension will not be enabled in virtual workspaces.")),
                            ]
                        },
                        description: {
                            type: 'string',
                            markdownDescription: ( localizeWithPath(
                                _moduleId,
                                64,
                                "A description of how virtual workspaces affects the extensions behavior and why it is needed. This only applies when `supported` is not `true`."
                            )),
                        }
                    }
                },
                untrustedWorkspaces: {
                    description: ( localizeWithPath(
                        _moduleId,
                        65,
                        'Declares how the extension should be handled in untrusted workspaces.'
                    )),
                    type: 'object',
                    required: ['supported'],
                    defaultSnippets: [
                        { body: { supported: '${1:limited}', description: '${2}' } },
                    ],
                    properties: {
                        supported: {
                            markdownDescription: ( localizeWithPath(
                                _moduleId,
                                66,
                                "Declares the level of support for untrusted workspaces by the extension."
                            )),
                            type: ['string', 'boolean'],
                            enum: ['limited', true, false],
                            enumDescriptions: [
                                ( localizeWithPath(
                                    _moduleId,
                                    67,
                                    "The extension will be enabled in untrusted workspaces with some functionality disabled."
                                )),
                                ( localizeWithPath(
                                    _moduleId,
                                    68,
                                    "The extension will be enabled in untrusted workspaces with all functionality enabled."
                                )),
                                ( localizeWithPath(
                                    _moduleId,
                                    69,
                                    "The extension will not be enabled in untrusted workspaces."
                                )),
                            ]
                        },
                        restrictedConfigurations: {
                            description: ( localizeWithPath(
                                _moduleId,
                                70,
                                "A list of configuration keys contributed by the extension that should not use workspace values in untrusted workspaces."
                            )),
                            type: 'array',
                            items: {
                                type: 'string'
                            }
                        },
                        description: {
                            type: 'string',
                            markdownDescription: ( localizeWithPath(
                                _moduleId,
                                71,
                                "A description of how workspace trust affects the extensions behavior and why it is needed. This only applies when `supported` is not `true`."
                            )),
                        }
                    }
                }
            }
        },
        sponsor: {
            description: ( localizeWithPath(
                _moduleId,
                72,
                "Specify the location from where users can sponsor your extension."
            )),
            type: 'object',
            defaultSnippets: [
                { body: { url: '${1:https:}' } },
            ],
            properties: {
                'url': {
                    description: ( localizeWithPath(
                        _moduleId,
                        73,
                        "URL from where users can sponsor your extension. It must be a valid URL with a HTTP or HTTPS protocol. Example value: https://github.com/sponsors/nvaccess"
                    )),
                    type: 'string',
                }
            }
        },
        scripts: {
            type: 'object',
            properties: {
                'vscode:prepublish': {
                    description: ( localizeWithPath(
                        _moduleId,
                        74,
                        'Script executed before the package is published as a VS Code extension.'
                    )),
                    type: 'string'
                },
                'vscode:uninstall': {
                    description: ( localizeWithPath(
                        _moduleId,
                        75,
                        'Uninstall hook for VS Code extension. Script that gets executed when the extension is completely uninstalled from VS Code which is when VS Code is restarted (shutdown and start) after the extension is uninstalled. Only Node scripts are supported.'
                    )),
                    type: 'string'
                }
            }
        },
        icon: {
            type: 'string',
            description: ( localizeWithPath(_moduleId, 76, 'The path to a 128x128 pixel icon.'))
        },
        l10n: {
            type: 'string',
            description: ( localizeWithPath(
                _moduleId,
                77,
                'The relative path to a folder containing localization (bundle.l10n.*.json) files. Must be specified if you are using the vscode.l10n API.'
            ))
        },
        pricing: {
            type: 'string',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                78,
                'The pricing information for the extension. Can be Free (default) or Trial. For more details visit: https://code.visualstudio.com/api/working-with-extensions/publishing-extension#extension-pricing-label'
            )),
            enum: ['Free', 'Trial'],
            default: 'Free'
        }
    }
};
class ExtensionsRegistryImpl {
    constructor() {
        this._extensionPoints = ( (new Map()));
    }
    registerExtensionPoint(desc) {
        if (( (this._extensionPoints.has(desc.extensionPoint)))) {
            throw ( (new Error('Duplicate extension point: ' + desc.extensionPoint)));
        }
        const result = ( (new ExtensionPoint(desc.extensionPoint, desc.defaultExtensionKind)));
        this._extensionPoints.set(desc.extensionPoint, result);
        if (desc.activationEventsGenerator) {
            ImplicitActivationEvents.register(desc.extensionPoint, desc.activationEventsGenerator);
        }
        schema.properties['contributes'].properties[desc.extensionPoint] = desc.jsonSchema;
        schemaRegistry.registerSchema(schemaId, schema);
        return result;
    }
    getExtensionPoints() {
        return Array.from(( (this._extensionPoints.values())));
    }
}
const PRExtensions = {
    ExtensionsRegistry: 'ExtensionsRegistry'
};
Registry.add(PRExtensions.ExtensionsRegistry, ( (new ExtensionsRegistryImpl())));
const ExtensionsRegistry = ( (Registry.as(PRExtensions.ExtensionsRegistry)));
schemaRegistry.registerSchema(schemaId, schema);
schemaRegistry.registerSchema(productSchemaId, {
    properties: {
        extensionEnabledApiProposals: {
            description: ( localizeWithPath(
                _moduleId,
                79,
                "API proposals that the respective extensions can freely use."
            )),
            type: 'object',
            properties: {},
            additionalProperties: {
                anyOf: [{
                        type: 'array',
                        uniqueItems: true,
                        items: {
                            type: 'string',
                            enum: ( (Object.keys(allApiProposals))),
                            markdownEnumDescriptions: ( (Object.values(allApiProposals)))
                        }
                    }]
            }
        }
    }
});
export { ExtensionMessageCollector, ExtensionPoint, ExtensionPointUserDelta, ExtensionsRegistry, ExtensionsRegistryImpl, schema };
